import { motion, AnimatePresence } from 'framer-motion';
import type { ImageMetadata } from '@/types/story.types';
import { FramedOverlay } from '@/components/Overlay/FramedOverlay';

interface NarrativeImageProps {
  image: ImageMetadata;
}

/**
 * NarrativeImage component
 * Displays an image overlay during a specific narrative
 *
 * Supports two modes:
 * 1. Simple overlay: Just the image (default behavior)
 * 2. Framed overlay: Image displayed within a decorative frame (polaroid, hands, etc.)
 */
export function NarrativeImage({ image }: NarrativeImageProps) {
  // Determine if we should use framed overlay
  const hasFrame = image.frame || image.overlay;

  // If framed overlay, use FramedOverlay component
  if (hasFrame) {
    const frameAssetUrl = image.frame || image.overlay?.frameAsset || '';
    const animation = image.overlay?.animation || 'slide-up-breath';
    const contentPosition = image.overlay?.contentPosition || 'contain';

    // Construct full URL for frame asset
    // Extract story path (everything before /images/)
    const storyPath = image.url.substring(0, image.url.indexOf('/images/'));
    const frameUrl = `${storyPath}/frames/${frameAssetUrl}`;

    console.log('[NarrativeImage] Using framed overlay:', {
      contentUrl: image.url,
      frameUrl,
      animation
    });

    return (
      <AnimatePresence>
        <FramedOverlay
          key={image.url}
          frameAssetUrl={frameUrl}
          contentImageUrl={image.url}
          animation={animation}
          opacity={image.opacity ?? 1.0}
          contentPosition={contentPosition}
          onEnterComplete={() => {
            console.log('[NarrativeImage] Framed overlay enter complete');
            // TODO: Play audio cue if specified (image.overlay?.audioEnter)
          }}
          onExitComplete={() => {
            console.log('[NarrativeImage] Framed overlay exit complete');
            // TODO: Play audio cue if specified (image.overlay?.audioExit)
          }}
        />
      </AnimatePresence>
    );
  }

  // Default: Simple overlay (original behavior)
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.5, // Default 500ms fade in/out
          ease: 'easeInOut'
        }}
        className="absolute inset-0 z-40 pointer-events-none flex items-center justify-center p-4 sm:p-8 md:p-12"
      >
        <img
          src={image.url}
          alt="Narrative scene"
          className="max-w-full max-h-full object-contain rounded-xl shadow-[0_0_50px_rgba(0,0,0,0.5)] drop-shadow-2xl"
          style={{
            opacity: image.opacity ?? 1.0,
          }}
          onLoad={() => console.log('[NarrativeImage] Image loaded:', image.url)}
          onError={(e) => {
            console.error('[NarrativeImage] Failed to load image:', image.url);
            console.error('[NarrativeImage] Error:', e);
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
}
